import {
  sizing,
  fontSizing,
  fontWeights,
  Button,
  ButtonType,
  CheckboxInput,
  Input,
  useToast,
  ReasonModal,
  useModal,
  AttentionType,
  breakpoints,
} from '@chillibean/component-library';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  useGetSubjectRequirementsQuery,
  useUpdateSubjectRequirementsMutation,
} from '../../redux/api/administrationApi.slice';
import { selectInternalResult } from '../../redux/internal.slice';
import SubjectRequirementsContainerLoading from './SubjectRequirementsContainerLoading';

export function SubjectRequirementsContainer() {
  // Store
  const { accountId, authenticityToken } = useSelector(selectInternalResult);
  // Store Queries
  const { data, isLoading } = useGetSubjectRequirementsQuery({ accountId: accountId }, { skip: !accountId });
  // Store Mutations
  const [updateSubjectRequirements, { isLoading: mutationLoading, error: mutationError }] =
    useUpdateSubjectRequirementsMutation();

  // Form
  const formMethods = useForm({
    defaultValues: {
      includeSitePrefix: false,
      numberOfCharacters: 8,
    },
  });
  const { reset, setValue, watch } = formMethods;
  const getFieldValue = (fieldName) => watch(fieldName);

  // Effects
  useEffect(() => {
    if (!isLoading && data) {
      reset(data, { keepDirtyValues: true });
    }
  }, [isLoading, data, reset]);

  // Custom
  const { showErrorToast, showSuccessToast } = useToast();
  const { closeModal: closeReasonModal, modalOpen: reasonModalOpen, openModal: openReasonModal } = useModal();

  // METHODS
  const handleUpdateIncludeSitePrefix = () => {
    const currentValue = getFieldValue('includeSitePrefix');

    setValue('includeSitePrefix', !currentValue);
  };

  const handleUpdateSubjectRequirements = async (params = {}) => {
    const { reason } = params;
    const includeSitePrefix = getFieldValue('includeSitePrefix');
    const numberOfCharacters = getFieldValue('numberOfCharacters');

    try {
      await updateSubjectRequirements({
        accountId: accountId,
        body: {
          authenticityToken: authenticityToken,
          includeSitePrefix: includeSitePrefix,
          numberOfCharacters: numberOfCharacters,
          reasonForChange: reason,
        },
      }).unwrap();

      showSuccessToast('Subject Number Requirements Updated');
      closeReasonModal();
    } catch (error) {
      showErrorToast(error);
    }
  };

  if (isLoading || !data) {
    return <SubjectRequirementsContainerLoading />;
  }

  return (
    <>
      <ReasonModal
        attentionText="Hold on! Editing this subject's number requirements may affect existing data within the subject."
        attentionType={AttentionType.WARNING}
        error={mutationError?.data?.errors?.message}
        isLoading={mutationLoading}
        onClose={closeReasonModal}
        onConfirm={handleUpdateSubjectRequirements}
        title="Edit Subject Number Requirements"
        visible={reasonModalOpen}
      />
      <FormContainer>
        <Header>Subject Number Requirements</Header>
        <CheckboxInput
          id="includeSitePrefix"
          label="Include Site number as Subject number prefix?"
          checked={!isLoading && getFieldValue('includeSitePrefix')}
          onChange={handleUpdateIncludeSitePrefix}
          name="include-site-prefix"
        />
        <StyledInput>
          <Input
            id="numberOfCharacters"
            maximumValue={250}
            minimumValue={1}
            name="number-of-characters"
            value={!isLoading && getFieldValue('numberOfCharacters')}
            type="number"
            onChange={(e) => setValue('numberOfCharacters', e.target.value)}
            label="Number of characters"
          />
        </StyledInput>
        <ButtonWrapper>
          <Button disabled={false} onClick={openReasonModal} text="Save" type={ButtonType.PRIMARY} />
        </ButtonWrapper>
      </FormContainer>
    </>
  );
}

const FormContainer = styled.div`
  padding: ${sizing.small};
  display: flex;
  flex-direction: column;
  gap: ${sizing.xlarge};
`;

const Header = styled.div`
  font-size: ${fontSizing.xlarge};
  font-weight: ${fontWeights.bold};
`;

const StyledInput = styled.div`
  width: 100%;

  @media (min-width: ${breakpoints.tablet}) {
    width: 400px;
  }
`;

const ButtonWrapper = styled.div``;

export default SubjectRequirementsContainer;
